import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import type { Tables } from "@/integrations/supabase/types";

type Location = Tables<"locations">;

export function useLocations() {
  const { business } = useBusiness();
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLocations = useCallback(async () => {
    if (!business) {
      setLocations([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: locationsError } = await supabase
        .from("locations")
        .select("*")
        .eq("business_id", business.id)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (locationsError) throw locationsError;

      setLocations(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch locations");
    } finally {
      setLoading(false);
    }
  }, [business]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  return {
    locations,
    loading,
    error,
    refetch: fetchLocations,
  };
}
